import { useEffect, useRef } from "react";
import { QueryKey, useQueryClient } from "@tanstack/react-query";
import {
  INotificationBaseRelatedToShow,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import uniqid from "uniqid";
import { apiV2 } from "api/firebase";

type UseInvalidateOnShowNotificationsArgs = {
  showId?: string;
  keys: NotificationType[];
  queryKey: QueryKey;
};

const useInvalidateOnShowNotifications = ({
  showId,
  keys,
  queryKey,
}: UseInvalidateOnShowNotificationsArgs): void => {
  const queryClient = useQueryClient();
  const listenerIdRef = useRef(uniqid());
  const serializedQueryKey = JSON.stringify(queryKey);

  useEffect(() => {
    if (!showId) return undefined;

    const listenerId = listenerIdRef.current;
    const scopedQueryKey = JSON.parse(serializedQueryKey) as QueryKey;
    const invalidate = async (payload: INotificationBaseRelatedToShow) => {
      if (payload.showId !== showId) return;
      await queryClient.invalidateQueries(
        { queryKey: scopedQueryKey },
        { cancelRefetch: false }
      );
    };

    keys.forEach((key) =>
      apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
        key,
        showId,
        listenerId,
        callback: invalidate,
      })
    );

    return () => {
      keys.forEach((key) =>
        apiV2.notification.unsubscribe({ key, showId, listenerId })
      );
    };
  }, [showId, keys, serializedQueryKey, queryClient]);
};

export default useInvalidateOnShowNotifications;
